import React, { useContext, useEffect, useMemo } from 'react'
import { Accordion, AccordionDetails, AccordionSummary, Autocomplete, Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import contactStyle from "../../../../../../assets/styles/componentStyles/campaignContactStyles/campaignContactStyle.module.scss";
import styles from "../../../../../../assets/styles/componentStyles/accountInformationCard.module.scss";
import globalStyles from "../../../../../../assets/styles/global.module.scss";
import campaignStyles from "../../../../../../assets/styles/componentStyles/whatsappCampaignStyles/whatsappCampaignStyle.module.scss";
import { useShallow } from 'zustand/react/shallow';
import { useIntegrationStore } from '../../../../../../store/integrationStore';
import { UserContext, UserContextPayload } from '../../../../../../context/userContext';
import { IntegrationContext, IntegrationContextPayload } from '../../../../../../context/integrationContext';
import { agentActivityMessage, agentActivityMessageTypes, agentActivityMessageVariable, agentActivityMessageVariableType } from '../../../../../../models/axiosModel';
import { AGENT_ACTIVITY_MESSAGE_TYPES } from '../whatsAppIntegrationConstant';
import Required from '../../../../../utility/required';
import GlobalButton from '../../../../../utility/globalButton/button';
import { useFlowBuilderStore } from '../../../../../../store/flowBuilderStore';
import { MetadataType } from '../../../../../../models/flowBuilderTypes';
import { useInsertVariableAtCursorPosition } from '../../../../../../hooks/useInsertVariableAtCursorPosition';
import { useGenericStore } from '../../../../../../store/genericStore';
import ConfigurationSection from '../../../../chatbot/configurationPopup/ConfigurationSection';

interface AgentActivityMessagesProps {
    agentAcitvityMsg: agentActivityMessage[], 
    setAgentAcitvityMsg: React.Dispatch<React.SetStateAction<agentActivityMessage[]>>,
    channelName: string,
    configureAgentActivityMsg: (flagSendAgentActicityMsg: boolean, flagDisableAgentOnUserInactivity: boolean) => Promise<any>,
    flagDisableAgentOnUserInactivity: boolean,
    onCancel: () => void,
    setShowPhoneNumberSettingsPopup: React.Dispatch<React.SetStateAction<boolean>>,
}

type fieldValueType = "message" | "messageVariable" | "variableType" | "metaDataType" | "fallbackValue";

type fieldUpdateType = 
| { type: 'message', value: string, msgType: agentActivityMessageTypes  }
| { type: 'messageVariable', value: agentActivityMessageVariable[], msgType: agentActivityMessageTypes }
| { type: 'variableType', value: agentActivityMessageVariableType | null, msgType: agentActivityMessageTypes, variable: agentActivityMessageVariable }
| { type: 'metaDataType', value: MetadataType | null, msgType: agentActivityMessageTypes, variable: agentActivityMessageVariable }
| { type: 'fallbackValue', value: string, msgType: agentActivityMessageTypes, variable: agentActivityMessageVariable }

const AgentActivityMessages: React.FC<AgentActivityMessagesProps> = (props) => {
    
    const { agentAcitvityMsg, setAgentAcitvityMsg, channelName, configureAgentActivityMsg, flagDisableAgentOnUserInactivity, onCancel, setShowPhoneNumberSettingsPopup } = props;

    const { insertVariable, registerTextarea } = useInsertVariableAtCursorPosition();

    const { setFlagLoading, handleError } = useContext(UserContext) as UserContextPayload;
    const { globalIntegrationTypeList } = useContext(IntegrationContext) as IntegrationContextPayload;

    const { getAgentActivityMessageTypesApiCall, agentActivityMessageTypes, agentActivityMsgVariableTypes, getAgentActivityMsgVariableTypesApiCall } = useIntegrationStore(
        useShallow((state) => ({
            getAgentActivityMessageTypesApiCall: state.getAgentActivityMessageTypesApiCall,
            agentActivityMessageTypes: state.agentActivityMessageTypes,
            agentActivityMsgVariableTypes: state.agentActivityMsgVariableTypes,
            getAgentActivityMsgVariableTypesApiCall: state.getAgentActivityMsgVariableTypesApiCall,
        }))
    ); 
    const { metaDataVariableList, GetMetaDataVariableListApi } = useFlowBuilderStore(
        useShallow((state) => ({
            metaDataVariableList: state.metaDataVariableList,
            GetMetaDataVariableListApi: state.GetMetaDataVariableListApi,
        }))
    )

    const { showSuccess } = useGenericStore(useShallow((state) => ({
        showSuccess: state.showSuccess
    })))

    // Get Agent Activity Message Type Call (agent_assigned, agent_joined and agent_left)
    const getAgentActivityMessageTypes = async () => {
        setFlagLoading((prev) => ({ ...prev, flagRoundLoader: true }));
        const channelId = globalIntegrationTypeList?.find((integration) => integration?.channel_name?.toLowerCase() === channelName?.toLowerCase())?.channel_id ?? 1;
        const response = await getAgentActivityMessageTypesApiCall(channelId);
        if(!response?.status){
            handleError(response);
        }
        setFlagLoading((prev) => ({ ...prev, flagRoundLoader: false }));
    }

    // Get Agent Activity Message Variable Call
    const getAgentActivityMessageVariableTypes = async () => {
        setFlagLoading((prev) => ({ ...prev, flagRoundLoader: true }));
        const response = await getAgentActivityMsgVariableTypesApiCall();
        if(!response?.status){
            handleError(response);
        }
        setFlagLoading((prev) => ({ ...prev, flagRoundLoader: false }));
    }

    // Generic Meta-Data Variable Call  
    const getMetaDataVariableList = async () => {
        setFlagLoading((prev) => ({ ...prev, flagRoundLoader: true }));
        const response = await GetMetaDataVariableListApi();
        if(!response?.status){
            handleError(response);
        }
        setFlagLoading((prev) => ({ ...prev, flagRoundLoader: false }));
    }

    useEffect(() => {
        if(!agentActivityMessageTypes){
            getAgentActivityMessageTypes();
        }
        if(!agentActivityMsgVariableTypes){
            getAgentActivityMessageVariableTypes();
        }
        if(!metaDataVariableList || metaDataVariableList?.length === 0){
            getMetaDataVariableList();
        }
    }, []);

    useEffect(() => {
        if(agentActivityMessageTypes && agentActivityMessageTypes?.length > 0){
            const updatedAgentActivityMsg = [...agentAcitvityMsg];

            agentActivityMessageTypes?.map((messageType) => {
                if(!updatedAgentActivityMsg?.find((message) => message?.agent_activity_message_type_id === messageType?.agent_activity_message_type_id)){
                    updatedAgentActivityMsg.push({
                        agent_activity_message_type_id: messageType?.agent_activity_message_type_id,
                        agent_activity_message_type_name: messageType?.agent_activity_message_type_name,
                        message: messageType?.default_message ?? '',
                        agent_activity_message_variable: messageType?.default_message_variables ?? [],
                    })
                }
            });
            
            if(updatedAgentActivityMsg?.length !== agentAcitvityMsg?.length){
                setAgentAcitvityMsg(updatedAgentActivityMsg);
            }
        }
    }, [agentActivityMessageTypes])

    const getConfigureAgentActivityMessageTitle = (messageType: agentActivityMessageTypes) => {
        let str = '';
        switch(messageType?.agent_activity_message_type_name){
            case AGENT_ACTIVITY_MESSAGE_TYPES['AGENT_ASSIGNED']:
                str = 'agent assignment';
                break;
            case AGENT_ACTIVITY_MESSAGE_TYPES['AGENT_JOINED']:
                str = 'agent joining';
                break;
            case AGENT_ACTIVITY_MESSAGE_TYPES['AGENT_LEFT']:
                str = 'agent leaving';
                break;
            default:
                str = '';
        }
        return `Configure message for ${str}`;
    }

    const metaDataTypeList = useMemo(() => {
        return metaDataVariableList?.find((metaData) => metaData?.channel_name?.toLowerCase() === channelName?.toLowerCase())?.metadata_type ?? [];
    }, [metaDataVariableList])

    const isVariableTypeMetadata = (variableTypeName: string) => {
        return variableTypeName?.toLowerCase() === 'metadata';
    }

    const getFieldValue = (type: fieldValueType, messageType: agentActivityMessageTypes, variable?: agentActivityMessageVariable) => {
        const msg = agentAcitvityMsg?.find((message) => message?.agent_activity_message_type_id === messageType?.agent_activity_message_type_id);

        switch(type){
            case "message":
                return msg?.message ?? '';
            case "messageVariable":
                return msg?.agent_activity_message_variable ?? [];
            case "variableType":
                if(variable){
                    return agentActivityMsgVariableTypes?.find((varType) => String(varType?.agent_activity_message_variable_type_id) == String(variable?.agent_activity_message_variable_type_id)) ?? null;
                }
                return null;
            case "metaDataType":
                if(variable){
                    return metaDataTypeList?.find((metaDataType) => String(metaDataType?.metadata_type_id) == String(variable?.metadata_type_id)) ?? null;
                }
                return '';
            case "fallbackValue":
                if(variable){
                    return variable?.fallback_value ?? '';
                }
                return '';
            default:
                return null;
        }
    }

    const onChangeOfAgentActivityMessage = (messageType: agentActivityMessageTypes, value: string) => {
        const matchedVariables = value.match(/{{[a-zA-Z0-9_]+}}/g) || [];
        const uniqueVariables = Array.from(new Set(matchedVariables));

        const variableData = agentAcitvityMsg?.find((msg) => msg?.agent_activity_message_type_id === messageType?.agent_activity_message_type_id)?.agent_activity_message_variable ?? [];
        const mappedVariableData: agentActivityMessageVariable[] = uniqueVariables?.map((key, index) => {
            const isVariableExist = variableData?.find((variable: agentActivityMessageVariable) => variable?.variable_key === key);
            if(isVariableExist){
                return isVariableExist;
            }
            return {
                agent_activity_message_variable_type_id: null,
                agent_activity_message_variable_type_name: '',
                fallback_value: '',
                metadata_type_id: null,
                variable_key: key,
            }
        })

        onChangeOfField({ type: 'message', value: value, msgType: messageType });
        onChangeOfField({ type: 'messageVariable', value: mappedVariableData, msgType: messageType });
    }

    const onChangeOfField = (updateType: fieldUpdateType) => {
        const { type, value, msgType } = updateType;
        
        setAgentAcitvityMsg((prev) => {
            let updatedMsg = prev;

            switch(type){
                case 'message':
                    updatedMsg = prev?.map((msg) => {
                        if(msg?.agent_activity_message_type_id === msgType?.agent_activity_message_type_id){
                            return { ...msg, message: value };
                        }
                        return msg;
                    }) ?? [];
                    break;
                case 'messageVariable':
                    updatedMsg = prev?.map((msg) => {
                        if(msg?.agent_activity_message_type_id === msgType?.agent_activity_message_type_id){
                            return { ...msg, agent_activity_message_variable: value };
                        }
                        return msg;
                    }) ?? [];
                    break;
                case 'variableType':
                    updatedMsg = prev?.map((msg: agentActivityMessage) => {
                        if(msg?.agent_activity_message_type_id === msgType?.agent_activity_message_type_id){
                            return { 
                                ...msg, 
                                agent_activity_message_variable: msg?.agent_activity_message_variable?.map((variable) => {
                                    if(variable?.variable_key === updateType?.variable?.variable_key){
                                        return { 
                                            ...variable, 
                                            agent_activity_message_variable_type_id: value?.agent_activity_message_variable_type_id ?? null,
                                            agent_activity_message_variable_type_name: value?.agent_activity_message_variable_type_name ?? '',
                                            metadata_type_id: null,
                                        };
                                    }
                                    return variable;
                                })
                            };
                        }
                        return msg;
                    }) ?? [];
                    break;
                case 'metaDataType':
                    updatedMsg = prev?.map((msg: agentActivityMessage) => {
                        if(msg?.agent_activity_message_type_id === msgType?.agent_activity_message_type_id){
                            return { 
                                ...msg, 
                                agent_activity_message_variable: msg?.agent_activity_message_variable?.map((variable) => {
                                    if(variable?.variable_key === updateType?.variable?.variable_key){
                                        return { ...variable, metadata_type_id: value?.metadata_type_id ?? null };
                                    }
                                    return variable;
                                })
                            };
                        }
                        return msg;
                    }) ?? [];
                    break;
                case 'fallbackValue':
                    updatedMsg = prev?.map((msg: agentActivityMessage) => {
                        if(msg?.agent_activity_message_type_id === msgType?.agent_activity_message_type_id){
                            return { 
                                ...msg, 
                                agent_activity_message_variable: msg?.agent_activity_message_variable?.map((variable) => {
                                    if(variable?.variable_key === updateType?.variable?.variable_key){
                                        return { ...variable, fallback_value: value ?? '' };
                                    }
                                    return variable;
                                })
                            };
                        }
                        return msg;
                    }) ?? [];
                    break;
                default:
                    updatedMsg = prev;
                    break;
            }
            return updatedMsg
        })
    }

    const checkDisableSaveBtn = () => {
        return agentActivityMessageTypes?.some((msgType) => {
            if(!(getFieldValue("message", msgType) as string)?.trim()){
                return true;
            }
            return (getFieldValue("messageVariable", msgType) as agentActivityMessageVariable[])?.some((variable) => {
                if(isVariableTypeMetadata(variable?.agent_activity_message_variable_type_name) && !variable?.metadata_type_id){
                    return true;
                }

                if(!variable?.agent_activity_message_variable_type_id || !variable?.fallback_value?.trim()){
                    return true;
                }
                return false;
            })
        })
    }

    const onClickOfSave = async () => {
        const res = await configureAgentActivityMsg(true, flagDisableAgentOnUserInactivity);
        if(res?.status){
            showSuccess("Agent activity message configured successfully")
            setShowPhoneNumberSettingsPopup(false);
        } else {
            handleError(res);
        }
    }

    return (
        <>
            {agentActivityMessageTypes && 
                <Grid item xs={12} className={styles.agentActivityMsgContainer}>
                    <Grid container className={`${globalStyles.flexColumn} ${globalStyles.justifyCenter}`}>
                        <Grid item className={globalStyles.fullwidth}>
                            <div className={styles.agentActivityMsgScrollBox}>
                                <Grid container gap={2}>
                                    {agentActivityMessageTypes?.map((messageType, index) => {
                                        return (
                                            <Grid item className={globalStyles.fullwidth} xs={12} key={messageType?.agent_activity_message_type_id}>
                                                <Accordion
                                                    defaultExpanded={index === 0}
                                                    className={styles.agentActivityMsgAccordion}
                                                    disableGutters
                                                >
                                                    <AccordionSummary
                                                        expandIcon={<ExpandMoreIcon />}
                                                        className={styles.agentActivityMsgAccordionSummary}
                                                    >
                                                        <ConfigurationSection
                                                            title={getConfigureAgentActivityMessageTitle(messageType)}
                                                            isSectionRequired
                                                        />
                                                    </AccordionSummary>

                                                    <AccordionDetails className={styles.agentActivityMsgAccordionDetails}>
                                                        <ConfigurationSection>
                                                            <Grid container gap={2} direction={"column"}>
                                                                <Grid item className={globalStyles.fullwidth} xs={12}>
                                                                    <textarea
                                                                        ref={(el) => registerTextarea(messageType?.agent_activity_message_type_id, el)}
                                                                        rows={1}
                                                                        placeholder={`Enter Message`}
                                                                        className={`${contactStyle.textareaStyle} ${globalStyles.fullwidth} ${styles.agentActivityMsgTextarea}`}
                                                                        value={getFieldValue("message", messageType) as string}
                                                                        onChange={(e) => onChangeOfAgentActivityMessage(messageType, e.target.value)}
                                                                    />
                                                                </Grid>

                                                                <Grid item className={globalStyles.fullwidth} xs={12}>
                                                                    <GlobalButton
                                                                        buttonText="Add Variable"
                                                                        sx={{ width: "130px", padding: "5px 10px !important", fontWeight: "500 !important" }}
                                                                        className='borderedButtonStyle'
                                                                        onMouseDown={(e) => e.preventDefault()}
                                                                        onClick={() => {
                                                                            const messageAfterInsertVariable = insertVariable('', messageType?.agent_activity_message_type_id, getFieldValue("message", messageType) as string ?? '');
                                                                            onChangeOfField({ type: 'message', value: messageAfterInsertVariable, msgType: messageType });
                                                                        }}
                                                                    />
                                                                </Grid>

                                                                {(getFieldValue("messageVariable", messageType) as agentActivityMessageVariable[])?.length > 0 && 
                                                                    <Grid item className={`${globalStyles.fullwidth} ${styles.agentActivityMsgVariableContainer}`} xs={12}>
                                                                        <ConfigurationSection
                                                                            title="Assign Values to Variables"
                                                                            isSectionRequired
                                                                        >
                                                                            <TableContainer className={`${globalStyles.fullwidth} ${globalStyles.maxWidthFull} ${globalStyles.overflowAuto} ${globalStyles.boxSizingBorderBox}`}>
                                                                                <Table>
                                                                                    <TableHead>
                                                                                        <TableRow>
                                                                                            <TableCell className={`${styles.agentActivityMsgTableHeadCell}`}>Variable</TableCell>
                                                                                            <TableCell className={`${styles.agentActivityMsgTableHeadCell}`}>Variable Type <Required /></TableCell>
                                                                                            <TableCell className={`${styles.agentActivityMsgTableHeadCell}`}>Meta Data Type <Required /></TableCell>
                                                                                            <TableCell className={`${styles.agentActivityMsgTableHeadCell}`}>Fallback Value <Required /></TableCell>
                                                                                        </TableRow>
                                                                                    </TableHead>
                                                                                    
                                                                                    <TableBody>
                                                                                        {(getFieldValue("messageVariable", messageType) as agentActivityMessageVariable[])?.map((variable, index) => {
                                                                                            return (
                                                                                                <TableRow key={index} className={campaignStyles.tableRow}>
                                                                                                    <TableCell className={campaignStyles.tableSubHeadingFontStyle}>
                                                                                                        {variable?.variable_key ?? ''}
                                                                                                    </TableCell>
                                                                                                    <TableCell className={`${campaignStyles.tableSubHeadingFontStyle} ${styles.agentActivityMsgTableCell}`}>
                                                                                                        <Autocomplete
                                                                                                            className={`${contactStyle.contactFilterDropdownsInPopup} ${styles.agentActivityMsgAutocomplete}`}
                                                                                                            options={agentActivityMsgVariableTypes ?? []}
                                                                                                            getOptionLabel={(option) => option?.agent_activity_message_variable_type_name || ''}
                                                                                                            value={getFieldValue("variableType", messageType, variable) as agentActivityMessageVariableType | null}
                                                                                                            onChange={(_, value: agentActivityMessageVariableType | null) => onChangeOfField({ type: 'variableType', value: value, msgType: messageType, variable: variable })}
                                                                                                            ListboxProps={{ className: styles.agentActivityMsgAutocompleteListbox }}
                                                                                                            renderInput={(params) => <TextField {...params} placeholder="Select Variable Type" />}
                                                                                                        />
                                                                                                    </TableCell>
                                                                                                    <TableCell className={`${campaignStyles.tableSubHeadingFontStyle} ${styles.agentActivityMsgTableCell}`}>
                                                                                                        {isVariableTypeMetadata((getFieldValue("variableType", messageType, variable) as agentActivityMessageVariableType | null)?.agent_activity_message_variable_type_name ?? '') ?
                                                                                                            <Autocomplete
                                                                                                                className={`${contactStyle.contactFilterDropdownsInPopup} ${styles.agentActivityMsgAutocomplete}`}
                                                                                                                options={metaDataTypeList}
                                                                                                                getOptionLabel={(option) => option?.metadata_type_name || ''}
                                                                                                                value={getFieldValue("metaDataType", messageType, variable) as MetadataType | null}
                                                                                                                onChange={(_, value: MetadataType | null) => onChangeOfField({ type: 'metaDataType', value: value, msgType: messageType, variable: variable })}
                                                                                                                ListboxProps={{ className: styles.agentActivityMsgAutocompleteListbox }}
                                                                                                                renderInput={(params) => <TextField {...params} placeholder="Select Meta Data Type" />}
                                                                                                            />
                                                                                                            :
                                                                                                            "Not Applicable"
                                                                                                        }
                                                                                                    </TableCell>
                                                                                                    <TableCell className={`${campaignStyles.tableSubHeadingFontStyle} ${styles.agentActivityMsgTableCell}`}>
                                                                                                        <TextField
                                                                                                            value={getFieldValue("fallbackValue", messageType, variable) as string}
                                                                                                            placeholder='Enter Fallback Value'
                                                                                                            onChange={(e) => onChangeOfField({ type: 'fallbackValue', value: e.target.value, msgType: messageType, variable: variable })}
                                                                                                            className={`${campaignStyles.whatsappCampaignTextField} ${styles.agentActivityMsgTextField}`}
                                                                                                        />
                                                                                                    </TableCell>
                                                                                                </TableRow>
                                                                                            );
                                                                                        })}
                                                                                    </TableBody>
                                                                                </Table>
                                                                            </TableContainer>
                                                                        </ConfigurationSection>
                                                                    </Grid>
                                                                }
                                                            </Grid>
                                                        </ConfigurationSection>
                                                    </AccordionDetails>
                                                </Accordion>
                                            </Grid>
                                        );
                                    })}
                                </Grid>
                            </div>
                        </Grid>

                        <Grid item mt={2} className={styles.agentActivityMsgFooter}>
                            <Grid container justifyContent="center" gap={2}>
                                <GlobalButton
                                    className="borderedButtonStyle"
                                    buttonText="Cancel"
                                    sx={{ width: "120px", flexShrink: 0 }}
                                    onClick={onCancel}
                                />

                                <GlobalButton
                                    className="primaryButtonStyle"
                                    buttonText="Save"
                                    sx={{ width: "120px", flexShrink: 0 }}
                                    disabled={checkDisableSaveBtn()}
                                    onClick={() => onClickOfSave()}
                                />
                            </Grid>
                        </Grid>

                    </Grid>
                </Grid>
            }
        </>
    )
}

export default AgentActivityMessages
